import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  addChatsToProject,
  bulkRemoveProjectChats,
  getProjectChats,
  getProjectChatStats,
} from '../../../services/projectService';
import groupService from '../../../services/groupService';

const DEFAULT_PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 300;

const FILTER_KEYS = ['search', 'category', 'language', 'country', 'assignment_type'];

const INITIAL_FILTERS = {
  search: '',
  category: '',
  language: '',
  country: '',
  assignment_type: '',
};

function readFiltersFromUrl(searchParams) {
  const next = { ...INITIAL_FILTERS };
  for (const key of FILTER_KEYS) {
    const raw = searchParams.get(key);
    if (raw) {
      next[key] = raw;
    }
  }
  return next;
}

function writeFiltersToUrl(searchParams, filters) {
  const next = new URLSearchParams(searchParams);
  for (const key of FILTER_KEYS) {
    const value = filters[key];
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
  }
  // filters reset pagination to page 1 implicitly (handled by useEffect)
  next.set('page', '1');
  return next;
}

function hasActiveFilters(filters) {
  return FILTER_KEYS.some((key) => Boolean(filters[key]));
}

function buildServiceFilters(filters) {
  const out = {};
  for (const key of FILTER_KEYS) {
    if (filters[key]) {
      out[key] = filters[key];
    }
  }
  return out;
}

const useGroups = (projectId, setError, setSuccess) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [allGroups, setAllGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedChats, setSelectedChats] = useState([]);
  const [chats, setChats] = useState([]);
  const [chatStats, setChatStats] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [addGroupsDialogOpen, setAddGroupsDialogOpen] = useState(false);
  const [bulkRemoveDialogOpen, setBulkRemoveDialogOpen] = useState(false);

  // Filters: URL is the source of truth. `pendingSearch` holds the immediate
  // value of the input box; the URL only updates after debounce so we don't
  // spam the server on every keystroke.
  const [filters, setFilters] = useState(() => readFiltersFromUrl(searchParams));
  const [pendingSearch, setPendingSearch] = useState(() => searchParams.get('search') || '');
  const debounceTimerRef = useRef(null);

  const fetchProjectChats = useCallback(async () => {
    if (!projectId) {
      setChats([]);
      setPagination(null);
      return;
    }

    try {
      setGroupsLoading(true);
      const response = await getProjectChats(projectId, {
        page,
        pageSize,
        ...buildServiceFilters(filters),
      });
      setChats(Array.isArray(response?.chats) ? response.chats : []);
      setPagination(response?.pagination || null);
    } catch (err) {
      console.error('Error fetching project chats:', err);
      setChats([]);
      setPagination(null);
      setError('Failed to load project groups. Please try again later.');
    } finally {
      setGroupsLoading(false);
    }
  }, [page, pageSize, projectId, filters, setError]);

  const fetchChatStats = useCallback(async () => {
    if (!projectId) {
      setChatStats(null);
      return;
    }

    try {
      const stats = await getProjectChatStats(projectId);
      setChatStats(stats);
    } catch (err) {
      console.error('Error fetching project chat stats:', err);
      setChatStats(null);
    }
  }, [projectId]);

  const refreshProjectGroups = useCallback(async () => {
    await Promise.all([fetchProjectChats(), fetchChatStats()]);
  }, [fetchChatStats, fetchProjectChats]);

  useEffect(() => {
    void refreshProjectGroups();
  }, [refreshProjectGroups]);

  // Filters or pageSize change -> back to page 1.
  useEffect(() => {
    setPage(1);
  }, [projectId, pageSize, filters]);

  // When the user navigates between projects, the component instance is
  // reused (only the route param changes). Reset filters so we don't carry
  // search/filter state across projects.
  useEffect(() => {
    setSelectedChats([]);
    setSelectedGroups([]);
    setPendingSearch('');
    setFilters(INITIAL_FILTERS);
    setPage(1);
    setSearchParams(new URLSearchParams(), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    fetchAllGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const fetchAllGroups = async () => {
    try {
      const response = await groupService.getGroups(
        { subscription_status: 'subscribed' },
        { page: 1, pageSize: 500 }
      );
      const groups = response?.groups || response || [];
      setAllGroups(Array.isArray(groups) ? groups : []);
    } catch (err) {
      console.error('Error fetching groups:', err);
      setAllGroups([]);
    }
  };

  // Debounced search: input -> pendingSearch (immediate), URL update after 300ms.
  const handleSearchChange = useCallback(
    (nextValue) => {
      setPendingSearch(nextValue);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        const nextFilters = { ...filters, search: nextValue };
        setFilters(nextFilters);
        setSearchParams(writeFiltersToUrl(searchParams, nextFilters), { replace: true });
      }, SEARCH_DEBOUNCE_MS);
    },
    [filters, searchParams, setSearchParams]
  );

  const handleFilterChange = useCallback(
    (key, value) => {
      const nextFilters = { ...filters, [key]: value };
      setFilters(nextFilters);
      // Reset search to in-sync if the filter is search itself (immediate path).
      if (key === 'search') {
        setPendingSearch(value);
      }
      setSearchParams(writeFiltersToUrl(searchParams, nextFilters), { replace: true });
    },
    [filters, searchParams, setSearchParams]
  );

  const handleClearFilters = useCallback(() => {
    setPendingSearch('');
    setFilters(INITIAL_FILTERS);
    setSearchParams(writeFiltersToUrl(searchParams, INITIAL_FILTERS), { replace: true });
  }, [searchParams, setSearchParams]);

  // Cleanup pending debounce on unmount.
  useEffect(() => () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
  }, []);

  const handlePageChange = (nextPage) => {
    if (!pagination) {
      return;
    }

    if (nextPage < 1 || nextPage > pagination.totalPages) {
      return;
    }

    setPage(nextPage);
    setSelectedChats([]);
  };

  const handlePageSizeChange = (event) => {
    const nextPageSize = Number.parseInt(event.target.value, 10);
    setPageSize(Number.isFinite(nextPageSize) ? nextPageSize : DEFAULT_PAGE_SIZE);
    setSelectedChats([]);
  };

  const handleOpenAddGroupsDialog = () => {
    setSelectedGroups([]);
    setAddGroupsDialogOpen(true);
  };

  const handleCloseAddGroupsDialog = () => {
    setAddGroupsDialogOpen(false);
  };

  const handleAddGroups = async () => {
    if (selectedGroups.length === 0) {
      handleCloseAddGroupsDialog();
      return;
    }

    try {
      if (!projectId) {
        setError('Cannot add groups: Invalid project ID');
        return;
      }

      await addChatsToProject(projectId, selectedGroups.map((group) => group.chat_id));
      await refreshProjectGroups();
      handleCloseAddGroupsDialog();
      setSuccess('Groups added successfully!');
    } catch (err) {
      console.error('Error adding groups:', err);
      setError('Failed to add groups. Please try again later.');
    }
  };

  const handleRemoveChat = async (chatId) => {
    try {
      if (!projectId) {
        setError('Cannot remove group: Invalid project ID');
        return;
      }

      await bulkRemoveProjectChats(projectId, { chat_ids: [chatId] });
      setSelectedChats((current) => current.filter((id) => id !== chatId));
      await refreshProjectGroups();
      setSuccess('Group removed successfully!');
    } catch (err) {
      console.error('Error removing group:', err);
      setError('Failed to remove group. Please try again later.');
    }
  };

  const handleChatSelection = (chatId, isSelected) => {
    if (isSelected) {
      setSelectedChats((current) => [...current, chatId]);
    } else {
      setSelectedChats((current) => current.filter((id) => id !== chatId));
    }
  };

  const handleSelectAllChats = (isSelected) => {
    if (isSelected) {
      setSelectedChats(chats.map((chat) => chat.chat_id));
    } else {
      setSelectedChats([]);
    }
  };

  const handleOpenBulkRemoveDialog = () => {
    setBulkRemoveDialogOpen(true);
  };

  const handleCloseBulkRemoveDialog = () => {
    setBulkRemoveDialogOpen(false);
  };

  // Bulk-remove by selection OR by active filter. One endpoint, two entry
  // points — see projectService.bulkRemoveProjectChats.
  const handleBulkRemoveChats = async () => {
    const useFilters = selectedChats.length === 0 && hasActiveFilters(filters);
    const payload = useFilters
      ? { filters: buildServiceFilters(filters) }
      : { chat_ids: selectedChats };

    if (useFilters ? !hasActiveFilters(filters) : selectedChats.length === 0) {
      handleCloseBulkRemoveDialog();
      return;
    }

    try {
      if (!projectId) {
        setError('Cannot remove groups: Invalid project ID');
        return;
      }

      const result = await bulkRemoveProjectChats(projectId, payload);

      setSelectedChats([]);
      handleCloseBulkRemoveDialog();
      await refreshProjectGroups();

      const removedCount = result?.removed ?? 0;
      const truncatedSuffix = result?.truncated
        ? ' (capped at 5000 per request)'
        : '';
      setSuccess(
        useFilters
          ? `Removed ${removedCount} groups matching the active filters${truncatedSuffix}.`
          : `Removed ${removedCount} selected group${removedCount === 1 ? '' : 's'} successfully!`
      );
    } catch (err) {
      console.error('Error removing groups:', err);
      setError('Failed to remove groups. Please try again later.');
    }
  };

  const handleGroupSelection = (groups) => {
    setSelectedGroups(Array.isArray(groups) ? groups : []);
  };

  const getAvailableGroups = () => allGroups.filter(
    (group) => !chats.some((chat) => chat.chat_id === group.chat_id)
  );

  // Expose whether the dialog should describe the bulk action as "by filter"
  // so the UI can adapt the copy + confirmation.
  const bulkRemoveMode = useMemo(() => {
    if (selectedChats.length > 0) {
      return { mode: 'selected', count: selectedChats.length };
    }
    if (hasActiveFilters(filters)) {
      return { mode: 'filter', count: pagination?.totalCount ?? 0 };
    }
    return { mode: 'none', count: 0 };
  }, [selectedChats, filters, pagination]);

  return {
    addGroupsDialogOpen,
    allGroups,
    bulkRemoveDialogOpen,
    bulkRemoveMode,
    chatStats,
    chats,
    filters,
    pendingSearch,
    groupsLoading,
    page,
    pageSize,
    pagination,
    selectedChats,
    selectedGroups,
    getAvailableGroups,
    handleAddGroups,
    handleBulkRemoveChats,
    handleChatSelection,
    handleClearFilters,
    handleCloseAddGroupsDialog,
    handleCloseBulkRemoveDialog,
    handleFilterChange,
    handleGroupSelection,
    handleOpenAddGroupsDialog,
    handleOpenBulkRemoveDialog,
    handlePageChange,
    handlePageSizeChange,
    handleRemoveChat,
    handleSearchChange,
    handleSelectAllChats,
  };
};

export default useGroups;
